import { computed, ref, toValue } from 'vue';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { QueryGcTime } from '@/constants/http.js';
import { useTransfersStore, useTransferReservationsStore } from '@/stores/transfers';
import { TransferFileStatus, TransferStatus } from '@/constants/transfers.js';
import { useSearchAwareParams } from '@/composables/useSearchAwareParams.js';
import { sanitizeListParams } from '@/utils/query-params.js';

import api from './api';

export const useTransfers = () => {
  const { filters } = useTransfersStore();
  const searchParams = useSearchAwareParams({
    searchTerm: () => filters.search_text,
    defaultParams: filters,
    transformParams: {
      sort_by: 'relevance',
    },
  });

  return useQuery({
    queryKey: ['transfers', searchParams],
    queryFn: () => api.getTransfers(sanitizeListParams(searchParams.value)),
  });
};

export const useTransferById = (id, options = { gcTime: QueryGcTime, enablePolling: ref(false) }) => {
  return useQuery({
    queryKey: ['transfer', id],
    queryFn: () => api.getTransferById(toValue(id)),
    gcTime: options.gcTime ?? QueryGcTime,
    enabled: computed(() => !!id.value),
    refetchInterval: computed(() => (options.enablePolling?.value ? 5000 : false)),
  });
};

export const useTransferFiles = (id, status, options = { gcTime: QueryGcTime, poll: ref(false) }) => {
  return useQuery({
    queryKey: ['transfer-files', toValue(id), toValue(status)],
    queryFn: () => api.getTransferFiles(toValue(id)),
    gcTime: options.gcTime,
    enabled: computed(() =>
      [TransferStatus.RELEASED, TransferStatus.SETTLED, TransferStatus.CANCELED].includes(toValue(status))
    ),
    refetchInterval: ({ state }) => {
      const files = state.data?.data || [];
      const hasAnyProcessingFile = files.some((file) => file.status === TransferFileStatus.PROCESSING);
      return hasAnyProcessingFile ? 5000 : false;
    },
  });
};

export const useRequestTransferFile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, featureCategory }) => api.requestTransferFile(id, featureCategory),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['transfer-files', variables.id] });
    },
  });
};

export const useTransferReservations = (id) => {
  const { filters } = useTransferReservationsStore();
  const searchParams = useSearchAwareParams({
    searchTerm: () => filters.search_text,
    defaultParams: filters,
    transformParams: {
      sort_by: 'relevance',
    },
  });

  return useQuery({
    queryKey: ['transfer-reservations', id, searchParams],
    queryFn: () => api.getTransferReservations({ id: toValue(id), ...searchParams.value }),
    enabled: computed(() => !!id.value),
  });
};
